import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { FieldInfoGroupField, HiddenField } from '@app/common/configurable-wizards';
import {
  FieldInfoGroupSubFieldFragment,
  SelectableGroupFieldFragment,
} from '@app/common/configurable-wizards/queries/queryTyping/all-fields';

import { useSettingFieldsContext } from '@app/widgets/contacts-and-settings/contexts/SettingFieldsContext';
import { findField, FindFieldResult } from '@app/widgets/contacts-and-settings/utils/subFieldsFinder';

import { UrlParams } from '../types';

import { RenderSection } from '../utils/settingsSections';

export interface ContextField {
  readonly sectionId: string;
  readonly sectionField?: FindFieldResult;
  readonly currentSection?: RenderSection;
}

export const useUserSettingsContextField = (): ContextField => {
  const { sections } = useSettingFieldsContext();
  const { sectionId, fieldId } = useParams<UrlParams>();

  const currentSection = useMemo(
    () => sections.find(({ id }) => id === sectionId),
    [sections, sectionId],
  );

  const sectionField = useMemo(
    () => currentSection && findField(currentSection.fields, (field) => {
      // profile-security section fields
      if (field.id === fieldId) {
        return field;
      }

      // addresses section fields
      const hasAddressFields = (field as SelectableGroupFieldFragment)?.fieldGroups?.find(
        (item) => (item as FieldInfoGroupSubFieldFragment)?.subFields?.find(
          (subField) => (subField as HiddenField).sValue === fieldId,
        ),
      );

      if (hasAddressFields) {
        return field;
      }

      // contact-detail section fields
      const hasField = (field as FieldInfoGroupField)?.subFields?.find(
        (item) => (item as HiddenField).sValue === fieldId,
      );

      if (hasField) {
        return field;
      }

      return null;
    }),
    [currentSection, fieldId],
  );

  return {
    sectionId,
    currentSection,
    sectionField,
  };
};
